import $ from 'jquery';
import Logger from "./Logger";

declare var YorkPooling: any;
declare var renewalPageDetails: any;
declare var policyPageDetails: any;
declare var newbusinessTabsControl: any;
declare var AmendmentController: any;
declare var PolicyController: any;
declare var RenewalController: any;

interface ISubscriber {
    messageType: string;
    action: (message: any) => void;
}

export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting';

export default class WebSocketController {
    hubConnection: any;
    static subscribers: ISubscriber[];
    static connectionStatus: ConnectionStatus = 'disconnected';
    static statusChangeCallbacks: ((status: ConnectionStatus) => void)[] = [];

    constructor() {
        WebSocketController.subscribers = [];
    }

    static onStatusChange = (callback: (status: ConnectionStatus) => void) => {
        WebSocketController.statusChangeCallbacks.push(callback);
        // Immediately call with current status
        callback(WebSocketController.connectionStatus);
    };

    static offStatusChange = (callback: (status: ConnectionStatus) => void) => {
        const index = WebSocketController.statusChangeCallbacks.indexOf(callback);
        if (index > -1) {
            WebSocketController.statusChangeCallbacks.splice(index, 1);
        }
    };

    private static notifyStatusChange = (status: ConnectionStatus) => {
        WebSocketController.connectionStatus = status;
        WebSocketController.statusChangeCallbacks.forEach((callback) => callback(status));
    };

    initialize = (url: string, username: string) => {
        this.hubConnection = $.hubConnection(url, { useDefaultPath: false });
        this.hubConnection.qs = { userName: username };

        const hubProxy = this.hubConnection.createHubProxy('messageHub');
        hubProxy.on('processToast', this.processToast);
        hubProxy.on('processMessage', this.processMessage);

        Logger.Info(`SignalR connecting on ... ${this.hubConnection.url}`);
        WebSocketController.notifyStatusChange('connecting');
        this.hubConnection.logging = true;
        this.hubConnection.error(this.errorStatus);
        this.hubConnection.connectionSlow(this.slowConnectionStatus);
        this.hubConnection.reconnecting(this.reconnectHubStatus);
        this.hubConnection.reconnected(this.reconnectedStatus);
        this.hubConnection.disconnected(this.disconnectAndRetry);
        this.hubConnection
            .start({ transport: ['websockets', 'longPolling'], jsonp: true })
            .done(() => {
                Logger.Info(`SignalR connected via transport=${this.hubConnection.transport.name}&clientProtocol=${this.hubConnection.clientProtocol}`);
                WebSocketController.notifyStatusChange('connected');
            })
            .fail((ex: any) => {
                Logger.Info(`SignalR failed to connect: ${ex}`);
                WebSocketController.notifyStatusChange('disconnected');
            });
    };

    errorStatus = (error: any) => {
        Logger.Info(`SignalR ${error}`);
    };

    slowConnectionStatus = () => {
        Logger.Info('SignalR detected a slow connection...');
    };

    reconnectHubStatus = () => {
        Logger.Info('SignalR reconnecting...');
        WebSocketController.notifyStatusChange('connecting');
    };

    reconnectedStatus = () => {
        Logger.Info('SignalR reconnected.');
        WebSocketController.notifyStatusChange('connected');
    };

    disconnectAndRetry = () => {
        WebSocketController.notifyStatusChange('disconnected');
        setTimeout(() => {
            Logger.Info('SignalR disconnected. Retrying connection.');
            WebSocketController.notifyStatusChange('connecting');
            this.hubConnection
                .start({ transport: ['websockets', 'longPolling'], jsonp: true })
                .done(() => {
                    WebSocketController.notifyStatusChange('connected');
                })
                .fail(() => {
                    WebSocketController.notifyStatusChange('disconnected');
                });
        }, 5000);
    };

    static subscribeToMessage = (messageType: string, action: (message: any) => void) => {
        WebSocketController.subscribers.push({ messageType, action });
    };

    static unsubscribeToMessage = (messageType: string, action: (message: any) => void) => {
        const index = WebSocketController.subscribers.findIndex((x) => x.messageType === messageType && x.action === action);
        const subscribers = WebSocketController.subscribers;

        WebSocketController.subscribers = [...subscribers.slice(0, index), ...subscribers.slice(index + 1, subscribers.length)];
    };

    processToast = (message: any) => {
        global.console.log(`processToast received: ${message.messageType} ${message.messageText}`);
        const messageType = message.messageType;

        switch (messageType) {
            case 'Info':
                YorkPooling.qTip2.showMessageInfo(message.messageText);
                break;
            case 'Success':
                YorkPooling.qTip2.showMessageSuccess(message.messageText);
                break;
            case 'Error':
                YorkPooling.qTip2.showMessageError(message.messageText);
                break;
            case 'Warning':
                YorkPooling.qTip2.showMessageWarning(message.messageText);
                break;
            case 'Platinum':
                YorkPooling.qTip2.showMessagePlatinum(message.messageText);
                break;
        }
    };

    processMessage = (message: any) => {
        global.console.log(`processMessage received: ${message.actionType}`);

        const actions = WebSocketController.subscribers.filter((x) => x.messageType === message.actionType);
        for (let i = 0; i < actions.length; i++) {
            actions[i].action(message);
        }

        switch (message.actionType) {
            // Socket handler for refreshing batch renewals
            case 'RefreshRenewal':
                global.console.log('Refresh-Renewal');
                if (message.messageType === 'Success') {
                    YorkPooling.qTip2.showMessageSuccess(message.messageText);
                } else if (message.messageType === 'Error') {
                    YorkPooling.qTip2.showMessageError(message.messageText);
                }
                break;
            // Socket handler for refreshing individual renewals after saves
            case 'RefreshRatedRenewal':
                global.console.log('Refresh-RatedRenewal');
                // kick the renewal tab control
                RenewalController.ResetTabStatus();

                if (typeof renewalPageDetails !== 'undefined') {
                    renewalPageDetails.PerformCallback({ Command: 'refresh' }); // Force the controller method to invalidate anything cached in session..
                }
                break;
            // Socket handler for refreshing individual policy after saves
            case 'RefreshRatedPolicy':
                global.console.log('Refresh-RatedPolicy');

                // kick the policy tab control
                PolicyController.ResetTabStatus();

                if (typeof policyPageDetails !== 'undefined') {
                    policyPageDetails.PerformCallback({ Command: 'refresh' }); // Force the controller method to invalidate anything cached in session..
                }
                break;
            // Socket handler for refreshing individual renewals after saves
            case 'RefreshRatedNewBusiness':
                global.console.log('Refresh-RatedNewBusiness');

                // kick the policy tab control
                YorkPooling.NewBusiness.resetTabStatus();

                if (typeof newbusinessTabsControl !== 'undefined') {
                    newbusinessTabsControl.PerformCallback({ Command: 'refresh' }); // Force the controller method to invalidate anything cached in session..
                }
                break;
            // Socket handler for renewal binding
            case 'RedirectRatedRenewal':
                global.console.log('Redirect-RatedRenewal');
                RenewalController.RedirectRenewalToPolicy(message);
                break;
            // Socket handler for new business binding
            case 'RedirectBindNewBusiness':
                global.console.log('Redirect-BindNewBusiness');
                YorkPooling.NewBusiness.RedirectNewBusinessToPolicy(message);
                break;
            case 'Open-Document':
                global.console.log('Open-Document');
                YorkPooling.PolicyController.OpenDocumentToWindow(message);
                break;
            case 'GetReportFile':
                global.console.log('GetReportFile');
                PolicyController.CreateReport(message);
                break;
            case 'HandleExposureNotification':
                global.console.log('HandleExposureNotification');
                let ratedSuccessfully = true;
                if (message.messageType !== 'Success') {
                    ratedSuccessfully = false;
                }

                this.delayToastNotification(2000).then(() => {
                    this.showToastMessage(ratedSuccessfully, message.messageText, '#search_field', 3000);
                });

                break;
            case 'HandleBatchRateCompareComplete':
                global.console.log('BatchRatingCompareComplete');
                this.showToastMessage(message.messageType === 'Success', message.messageText);
                break;
            default:
                global.console.log('processMessage toast event was not handled correctly');
                break;
        }
    };

    delayToastNotification = (timeMs: number) => {
        return new Promise((resolve) => setTimeout(resolve, timeMs));
    };

    showToastMessage = (isSuccess: boolean, message: string, selector: string = '.qtipToastContainer', duration: number = 6000) => {
        if (isSuccess) {
            YorkPooling.qTip2.showMessageSuccess(message, selector, duration);
        } else {
            YorkPooling.qTip2.showMessageError(message, selector, duration);
        }
    };
}
